// src/App.jsx
import { useEffect } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import { useSelector } from "react-redux";

import AppRouter from "./router/AppRouter";

import NotificationPopup from "./notifications/NotificationPopup";
import { SocketProvider } from "./socket/SocketContext";
import { NotificationProvider } from "./popup/NotificationContext";
import AlarmProvider from "./hooks/AlarmProvider";
import { useGlobalAlarms } from "./hooks/useGlobalAlarms";
import CenteredAlarmPopup from "./popup/AlarmPopupSystem";
import CommandResponsePopup from "./popup/CommandResponsePopup";

import GlobalQuickModals from "./components/modals/GlobalQuickModals";
import { QuickModalProvider } from "./hooks/QuickModalContext";
import Navbar from "./mainframes/Navbar";
import { LanguageProvider } from "./i18n/LanguageProvider";
import CalendarPopupListener from "./mainframes/sidebar/CalendarPopupListener";
import NotificationSetup from "./components/NotificationSetup";
import { registerAxiosCrudSync } from "./utils/registerAxiosCrudSync";
import NavigationMonitor from "./router/NavigationMonitor";
import RouteErrorBoundary from "./router/RouteErrorBoundary";
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES } from "./config/googleMaps";

registerAxiosCrudSync();

// Helper component to run the background alarm logic
function AlarmMonitor() {
  useGlobalAlarms();
  return null;
}

/**
 * ✅ Global App Layout
 * Navbar yaha mount hoga → har page pe show
 * Agar login page pe hide chahiye to AppLayout me condition laga denge
 */
function AppLayout() {
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);
  const hideNavbar =
    ["/live-tracking", "/login", "/signup"].includes(location.pathname) ||
    (location.pathname === "/" && !currentUser);
  const isMapRoute =
    location.pathname === "/live-map" ||
    (location.pathname === "/dashboard" &&
      new URLSearchParams(location.search).get("tab") === "map");

  useEffect(() => {
    document.body.classList.toggle("gps-map-route", isMapRoute);
    document.body.classList.toggle("gps-non-map-route", !isMapRoute);

    return () => {
      document.body.classList.remove("gps-map-route");
      document.body.classList.remove("gps-non-map-route");
    };
  }, [isMapRoute]);

  return (
    <>
      <NavigationMonitor />
      {!hideNavbar ? <Navbar /> : null}
      <RouteErrorBoundary resetKey={`${location.pathname}${location.search}`}>
        <AppRouter />
      </RouteErrorBoundary>
    </>
  );
}

export default function App() {
  const { isLoaded, loadError } = useLoadScript({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  if (loadError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#ef4444",
        }}
      >
        ❌ Map loading failed. Please refresh the page.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#3b82f6",
        }}
      >
        🗺️ Loading Maps...
      </div>
    );
  }

  return (
       <LanguageProvider>
      <SocketProvider>
        <NotificationProvider>
          <AlarmProvider>
            <Router>
              <QuickModalProvider>
                {/* ✅ Background logic */}
                <AlarmMonitor />
                <NotificationSetup />

                {/* ✅ Global overlays */}
                <NotificationPopup />
                <CenteredAlarmPopup />
                <CommandResponsePopup />
                <CalendarPopupListener /> 

                {/* ✅ Global Modals */}
                <GlobalQuickModals />

                {/* ✅ Navbar + Routes globally */}
                <AppLayout />
              </QuickModalProvider>
            </Router>
          </AlarmProvider>
        </NotificationProvider>
      </SocketProvider>
      </LanguageProvider>
  );
}
